import { formatCents } from '@src/common';
import { resolveSaleChangeAmounts, type SaleChange } from '@src/domains/sale-changes';
import { getCouponDiscountCurrencyInfo } from '@src/domains/coupons';
import { CartCouponTypeEnum, PaymentOption } from '@src/core/buyerApiClient';
import type { PurchaseSummary } from '@src/common/types/purchaseSummary';
import useTranslate from '@src/i18n/useTranslate';
import React, { useMemo, useState } from 'react';
import ArrowDown from '@src/icons/arrow-down-icon.svg';
import { groupSaleItemsByProductPackage } from '@src/domains/cart';
import PackageItemsGroup from './Package/ProductPackageGroup';
import { SaleItemWithPrice } from './SaleItem';
import * as styles from '@src/components/Checkout/styles';
import { ServiceFeeDisplay } from '@src/components/reusable/ServiceFee';

const {
  CouponContainer,
  PurchaseResumeContent,
  TotalWithoutDiscountHeader,
  TotalWithoutDiscountValue,
  TotalWithoutDiscountContainer,
  TotalText,
  TotalPrice,
  Total,
  TotalContainer,
  Container,
  Header,
  ResumeTitle,
  ProductsResume,
  PurchaseResumeTotal,
  Coupon,
  CouponText,
  CouponCode,
  CouponValue,
  ReservationComparison,
  ReservationRow,
  CurrenctReservation,
  NewReservation,
} = styles;

export const PurchaseResume = ({ summary, saleChange, isResponsive = false, paymentOption }: PurchaseResumeProps) => {
  const { i18nElement, i18n } = useTranslate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { items, totalAmountCents, serviceFeeCurrencyInfo: serviceFee, coupon } = summary;

  const groupedSaleItems = useMemo(() => groupSaleItemsByProductPackage(items), [items]);

  const serviceFeeDescription = useMemo(
    () => summary.serviceFeeDescriptions.join(', ') || undefined,
    [summary.serviceFeeDescriptions],
  );

  const briefView = useMemo(() => !!saleChange, [saleChange]);

  const { downAmount, upAmount } = useMemo(() => resolveSaleChangeAmounts(saleChange), [saleChange]);

  return (
    <Container aria-label='resume container'>
      <Header isResponsive={isResponsive} onClick={() => (isResponsive ? setIsCollapsed((state) => !state) : null)}>
        <ResumeTitle>{i18nElement('checkout_purchase_summary')}</ResumeTitle>

        {isResponsive ? <ArrowDown /> : null}
      </Header>

      <PurchaseResumeContent isCollapsed={isCollapsed} isResponsive={isResponsive}>
        <ProductsResume aria-label='products resume' id='products_resume'>
          {groupedSaleItems.packages.map((packageItem, index) => (
            <PackageItemsGroup
              key={`${packageItem.productPackage.name}-${index}`}
              productPackage={packageItem.productPackage}
              items={packageItem.items}
            />
          ))}

          {groupedSaleItems.nonPackageItems?.map((item) => (
            <SaleItemWithPrice key={item.id} item={item} briefView={briefView} />
          ))}
        </ProductsResume>

        <PurchaseResumeTotal>
          {coupon && !briefView ? (
            <CouponContainer>
              <Coupon>
                <CouponText>{i18n('cart_coupon_text')}</CouponText>

                <CouponCode>{coupon.code}</CouponCode>
              </Coupon>

              <Coupon>
                <CouponText>{i18n('cart_discount')}</CouponText>

                <CouponValue>
                  {coupon?.type === CartCouponTypeEnum.Percentage ? (
                    <span>{`(-${coupon.formattedDiscount})`}</span>
                  ) : null}
                  {formatCents(getCouponDiscountCurrencyInfo(coupon).amountCents)}
                </CouponValue>
              </Coupon>
            </CouponContainer>
          ) : null}

          <TotalContainer divider={!saleChange}>
            <ServiceFeeDisplay serviceFeeCurrencyInfo={serviceFee} serviceFeeDescription={serviceFeeDescription} />

            {coupon && !briefView ? (
              <TotalWithoutDiscountContainer>
                <TotalWithoutDiscountHeader>{i18n('cart_totalLabel')}</TotalWithoutDiscountHeader>
                <TotalWithoutDiscountValue>
                  {formatCents(coupon.fullAmountCurrencyInfo.amountCents)}
                </TotalWithoutDiscountValue>
              </TotalWithoutDiscountContainer>
            ) : null}

            {briefView && downAmount !== undefined && upAmount !== undefined ? (
              <ReservationComparison>
                <ReservationRow>
                  <CurrenctReservation>{i18n('checkout_sale_change_previous_reservation')}</CurrenctReservation>
                  <CurrenctReservation>{formatCents(downAmount)}</CurrenctReservation>
                </ReservationRow>

                <ReservationRow>
                  <NewReservation>{i18n('checkout_sale_change_new_reservation')}</NewReservation>
                  <NewReservation>{formatCents(upAmount)}</NewReservation>
                </ReservationRow>
              </ReservationComparison>
            ) : null}

            <Total>
              <TotalText>{i18n(saleChange ? 'base_value' : 'cart_final_value')}</TotalText>

              <TotalPrice>{formatCents(paymentOption?.amountCurrencyInfo.amountCents ?? totalAmountCents)}</TotalPrice>
            </Total>
          </TotalContainer>
        </PurchaseResumeTotal>
      </PurchaseResumeContent>
    </Container>
  );
};

interface PurchaseResumeProps {
  summary: PurchaseSummary;
  saleChange?: SaleChange | null;
  isResponsive?: boolean;
  paymentOption: PaymentOption | undefined;
}
